import isString from 'lodash/isString';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import { isEmpty, sortBy } from 'lodash';
import {
  contractStatus,
  contractTypes,
  profiles
} from '../../constants/contracts';
import {
  differenceInMinutes,
  setHours,
  setMinutes,
  setSeconds
} from 'date-fns';
import { isArray } from 'lodash';
import { individualStatusTypes } from '../../constants/units';

const today = setHours(setMinutes(setSeconds(new Date(), 0), 0), 0);

export function getUnitContracts(state) {
  if (
    !state ||
    isEmpty(state) ||
    !state?.units?.currentUnit?.contracts?.length
  ) {
    return [];
  }
  return state.units.currentUnit.contracts;
}

export function getUnits(state) {
  return state.units.list.map(unit => {
    return {
      ...unit
    };
  });
}

export function isQueryUnitsInProgress(state) {
  return state.units.queryUnitsIsInProgress;
}

export function getQueryUnitsTotalPages(state) {
  return state.units.totalPages;
}

export function getQueryUnitsCurrentPage(state) {
  return state.units.currentPage;
}

export function getUnitById(state, id) {
  let unitId = id;
  if (isString(id)) {
    unitId = parseInt(id, 10);
  }
  const units = state.list;
  const result = units.filter(unit => unit.id === unitId);
  return result.length > 0 ? result[0] : null;
}

export function getCurrentUnit(state) {
  return state.units.currentUnit;
}

export function isDeleteUnitByIdInProgress(state) {
  return state.units.deleteUnitByIdIsInProgress;
}

export function getUnitOwners(unit) {
  if (!unit || isEmpty(unit) || !unit?.contracts?.length) {
    return [];
  } else {
    const ownersGroups = unit.individualGroups.filter(
      group => group.type === profiles.OWNER
    );

    const filteredContract = unit.contracts.filter(
      contract =>
        contract.status === contractStatus.VALID &&
        contract.type === contractTypes.PROPERTY_TITLE
    );

    const contractOwners = filteredContract
      ? filteredContract.map(e => e.ownersId).flat(2)
      : [];

    const individualOfContract = ownersGroups.find(group =>
      group.members.find(member => member.individual.id === contractOwners[0])
    );

    return individualOfContract
      ? individualOfContract.members
          .filter(
            member => member.individual.profiles[0].type === profiles.OWNER
          )
          .map(member => member.individual)
      : [];
  }
}

export function getRentedTotalPages(state) {
  return state.units.rentedUnitsTotalPages;
}

export function getRentedUnits(state) {
  return state.units.rentedUnits;
}

export function getAssignedUsersToUnit(unit) {
  if (isEmpty(unit)) {
    return [];
  }
  const unitGroup = unit.individualGroups
    .map(individual => individual.members)
    .flat(2);

  return unitGroup.map(member => member.individual);
}

export function getActiveRentalContract(contracts) {
  return contracts.find(
    contract =>
      contract.status === contractStatus.VALID &&
      contract.type !== contractTypes.PROPERTY_TITLE
  );
}

function getActivePropTitleContract(contracts) {
  return contracts.find(
    contract =>
      contract.status === contractStatus.VALID &&
      contract.type === contractTypes.PROPERTY_TITLE
  );
}

export function getAllContractResidents(unit, contracts) {
  const ownersAndCoresidents = getContractOwnersAndCoresidents(unit);
  const tenantsAndCoresidents = getContractTenantsAndCoresidents(unit);

  return {
    ...ownersAndCoresidents,
    ...tenantsAndCoresidents
  };
}

export function getAllIndividualsOnUnit(unit) {
  return unit.individualGroups
    .map(group => group.members.map(member => member.individual))
    .flat(2);
}

export function getDatesOfValidContracts(unit, contracts) {
  if (!isEmpty(contracts)) {
    const sortedContracts = sortBy(contracts, ['startDate']);
    return sortedContracts
      .filter(
        Contract =>
          Contract.type != contractTypes.PROPERTY_TITLE &&
          (Contract.status == contractStatus.CREATED ||
            Contract.status == contractStatus.VALID)
      )
      .map(contract => {
        return {
          contractId: contract.id,
          start: new Date(contract.startDate),
          end: new Date(contract.endDate)
        };
      });
  } else if (!isEmpty(unit?.contracts)) {
    const sortedContracts = sortBy(unit.contracts, ['startDate']);

    return sortedContracts
      .filter(
        Contract =>
          Contract.type != contractTypes.PROPERTY_TITLE &&
          (Contract.status == contractStatus.CREATED ||
            Contract.status == contractStatus.VALID)
      )
      .map(contract => {
        return {
          contractId: contract.id,
          start: new Date(contract.startDate),
          end: new Date(contract.endDate)
        };
      });
  } else {
    return [];
  }
}

export function getContractTenantsAndCoresidents(unit, contracts) {
  if (!unit || isEmpty(unit) || isEmpty(contracts)) {
    return {
      petsOfTenants: [],
      tenants: [],
      tenantsCoresidents: [],
      tenantsIndividualEnabled: false,
      tenantsAndCoresidentsOutOfContract: [],
      tenantsContract: false,
      allContractTenantMembers: [],
      vehiclesOfTenants: [],
      tenantsGroupId: false
    };
  } else {
    const activeRentalContract = isArray(contracts)
      ? getActiveRentalContract(contracts)
      : contracts;

    const individualOfContract = unit.individualGroups.find(
      group => group.contractId === activeRentalContract?.id
    );

    const tenantsAndCoresidents = individualOfContract?.members;

    if (isEmpty(tenantsAndCoresidents)) {
      return {
        petsOfTenants: [],
        tenants: [],
        tenantsCoresidents: [],
        tenantsIndividualEnabled: false,
        tenantsAndCoresidentsOutOfContract: [],
        tenantsContract: false,
        allContractTenantMembers: [],
        vehiclesOfTenants: [],
        tenantsGroupId: false
      };
    }

    const petsOfTenants = individualOfContract.pets;

    const vehiclesOfTenants = individualOfContract.vehicles;

    return {
      tenantsGroupId: individualOfContract.id,
      vehiclesOfTenants,
      petsOfTenants,
      tenants: tenantsAndCoresidents
        .filter(member =>
          activeRentalContract.tenantsId.includes(member.individual.id)
        )
        .map(member => ({
          ...member.individual,
          relationship: member.relationship,
          invitationCode: member.invitationCode
        })),

      tenantsCoresidents: tenantsAndCoresidents
        .filter(member =>
          activeRentalContract.coresidentsId.includes(member.individual.id)
        )
        .map(member => ({
          ...member.individual,
          relationship: member.relationship,
          invitationCode: member.invitationCode
        })),

      tenantsIndividualEnabled:
        individualOfContract.status == individualStatusTypes.ENABLED,

      tenantsAndCoresidentsOutOfContract: tenantsAndCoresidents
        .filter(
          member =>
            !activeRentalContract.coresidentsId.includes(
              member.individual.id
            ) && !activeRentalContract.tenantsId.includes(member.individual.id)
        )
        .map(member => ({
          ...member.individual,
          relationship: member.relationship,
          invitationCode: member.invitationCode
        })),
      tenantsContract: activeRentalContract,
      allContractTenantMembers: tenantsAndCoresidents.map(member => ({
        ...member.individual,
        relationship: member.relationship,
        invitationCode: member.invitationCode
      }))
    };
  }
}

export function getContractOwnersAndCoresidents(unit, contracts) {
  if (!unit || isEmpty(unit) || isEmpty(contracts)) {
    return {
      owners: [],
      ownersCoresidents: [],
      ownersIndividualEnabled: false,
      ownersAndCoresidentsOutOfContract: [],
      ownersContract: false,
      petsOfOwners: [],
      vehiclesOfOwners: [],
      allContractOwnerMembers: []
    };
  } else {
    const activePropTitleContract = isArray(contracts)
      ? getActivePropTitleContract(contracts)
      : contracts;
    const individualOfContract = unit.individualGroups.find(
      group => group.contractId === activePropTitleContract?.id
    );

    const ownersAndCoresidents = individualOfContract?.members;

    if (isEmpty(ownersAndCoresidents)) {
      return {
        owners: [],
        ownersCoresidents: [],
        ownersIndividualEnabled: false,
        ownersAndCoresidentsOutOfContract: [],
        ownersContract: false,
        petsOfOwners: [],
        vehiclesOfOwners: [],
        allContractOwnerMembers: []
      };
    }

    const petsOfOwners = individualOfContract.pets;

    const vehiclesOfOwners = individualOfContract.vehicles;

    return {
      vehiclesOfOwners,
      owners: ownersAndCoresidents
        .filter(member =>
          activePropTitleContract.ownersId.includes(member.individual.id)
        )
        .map(member => ({
          ...member.individual,
          relationship: member.relationship,
          invitationCode: member.invitationCode
        })),
      ownersCoresidents: ownersAndCoresidents
        .filter(member =>
          activePropTitleContract.coresidentsId.includes(member.individual.id)
        )
        .map(member => ({
          ...member.individual,
          relationship: member.relationship,
          invitationCode: member.invitationCode
        })),

      ownersIndividualEnabled:
        individualOfContract.status == individualStatusTypes.ENABLED,

      ownersAndCoresidentsOutOfContract: ownersAndCoresidents
        .filter(
          member =>
            !activePropTitleContract.coresidentsId.includes(
              member.individual.id
            ) &&
            !activePropTitleContract.ownersId.includes(member.individual.id)
        )
        .map(member => ({
          ...member.individual,
          relationship: member.relationship,
          invitationCode: member.invitationCode
        })),
      ownersContract: activePropTitleContract,
      petsOfOwners,
      allContractOwnerMembers: ownersAndCoresidents.map(member => ({
        ...member.individual,
        relationship: member.relationship,
        invitationCode: member.invitationCode
      }))
    };
  }
}

export function getSpecificContractUnitOwners(unit, contract) {
  if (!contract || isEmpty(contract) || !unit || isEmpty(unit)) {
    return [];
  } else {
    const ownersGroups = unit.individualGroups.filter(
      group => group.type === profiles.OWNER
    );

    const individualOfContract = ownersGroups.find(group =>
      group.members.find(
        member => member.individual.id === contract.ownersId[0]
      )
    );

    const response = individualOfContract.members
      .filter(member => contract.ownersId.includes(member.individual.id))
      .map(member => member.individual);

    return response;
  }
}

export function getSpecificContractUnitTenants(unit, contract) {
  if (!contract || isEmpty(contract) || !unit || isEmpty(unit)) {
    return [];
  } else {
    const tenantsGroups = unit.individualGroups.filter(
      group => group.type === profiles.TENANT
    );

    const individualOfContract = tenantsGroups.find(group =>
      group.members.find(
        member => member.individual.id === contract.tenantsId[0]
      )
    );

    const response = individualOfContract.members
      .filter(member => contract.tenantsId.includes(member.individual.id))
      .map(member => member.individual);

    return response;
  }
}

export function getContractUnitTenants(unit, contracts) {
  if (!contracts || isEmpty(contracts) || !unit || isEmpty(unit)) {
    return [];
  } else {
    const tenantsGroups = unit.individualGroups.filter(
      group => group.type === profiles.TENANT
    );

    if (!contracts || isEmpty(contracts)) {
      return;
    } else {
      const filteredContract = contracts.filter(
        contract =>
          contract.status === contractStatus.VALID &&
          contract.type != contractTypes.PROPERTY_TITLE
      );

      const contractTenants = filteredContract
        ? filteredContract.map(contract => contract.tenantsId).flat(2)
        : [];

      const individualOfContract = tenantsGroups.find(group =>
        group.members.find(
          member => member.individual.id === contractTenants[0]
        )
      );

      return individualOfContract
        ? individualOfContract.members.filter(
            member => member.individual.profiles[0].type === profiles.TENANT
          )
        : [];
    }
  }
}

export function getResidentsOfNextPropContractToStart(unit, contracts) {
  //gives the closest contract to start
  if (isEmpty(unit) || isEmpty(contracts)) {
    return {
      residentsOfNextPropContracToStart: [],
      propContractStartDate: false,
      nextPropContracToStart: false
    };
  }

  let i = 0;
  let flag = 0;
  let closestContract = {};

  const filteredContracts = contracts.filter(
    contract =>
      contract.status === contractStatus.CREATED &&
      contract.type === contractTypes.PROPERTY_TITLE
  );

  flag = Math.abs(
    differenceInMinutes(today, new Date(filteredContracts[0]?.startDate))
  );
  closestContract = filteredContracts[0];

  for (i = 0; i < filteredContracts.length; i++) {
    if (
      Math.abs(
        differenceInMinutes(today, new Date(filteredContracts[i]?.startDate))
      ) <= flag
    ) {
      flag = Math.abs(
        differenceInMinutes(today, new Date(filteredContracts[i]?.startDate))
      );
      closestContract = filteredContracts[i];
    }
  }

  const individualOfContract = unit.individualGroups.find(
    group => group.contractId === closestContract?.id
  );

  if (isEmpty(individualOfContract)) {
    return {
      residentsOfNextPropContracToStart: [],
      propContractStartDate: false,
      nextPropContracToStart: false
    };
  }

  return {
    residentsOfNextPropContracToStart: individualOfContract.members.map(
      member => ({
        ...member.individual,
        relationship: member.relationship,
        invitationCode: member.invitationCode
      })
    ),
    propContractStartDate: closestContract?.startDate,
    nextPropContracToStart: closestContract
  };
}

export function getResidentsOfNextRentalContractToStart(unit, contracts) {
  //gives the closest contract to start
  if (isEmpty(unit) || isEmpty(contracts)) {
    return {
      residentsOfNextRentalContracToStart: [],
      rentalContracType: false,
      rentalContractStartDate: false,
      rentalContractEndDate: false,
      nextTenantsGroupId: false
    };
  }

  let i = 0;
  let flag = 0;
  let closestContract = {};

  const filteredContracts = contracts.filter(
    contract =>
      contract.status === contractStatus.CREATED &&
      contract.type !== contractTypes.PROPERTY_TITLE
  );

  flag = Math.abs(
    differenceInMinutes(today, new Date(filteredContracts[0]?.startDate))
  );
  closestContract = filteredContracts[0];

  for (i = 0; i < filteredContracts.length; i++) {
    if (
      Math.abs(
        differenceInMinutes(today, new Date(filteredContracts[i]?.startDate))
      ) <= flag
    ) {
      flag = Math.abs(
        differenceInMinutes(today, new Date(filteredContracts[i]?.startDate))
      );
      closestContract = filteredContracts[i];
    }
  }

  const individualOfContract = unit.individualGroups.find(
    group => group.contractId === closestContract?.id
  );

  if (isEmpty(individualOfContract)) {
    return {
      residentsOfNextRentalContracToStart: [],
      rentalContracType: false,
      rentalContractStartDate: false,
      rentalContractEndDate: false,
      nextRentalContracToStart: false,
      nextTenantsGroupId: false
    };
  }

  return {
    nextTenantsGroupId: individualOfContract.id,
    residentsOfNextRentalContracToStart: individualOfContract.members.map(
      member => ({
        ...member.individual,
        relationship: member.relationship,
        invitationCode: member.invitationCode
      })
    ),
    rentalContractStartDate: closestContract?.startDate,
    rentalContractEndDate:
      closestContract?.type != contractTypes.PROPERTY_TITLE
        ? closestContract?.endDate
        : false,
    rentalContracType: closestContract?.type,
    nextRentalContracToStart: closestContract
  };
}

export function getNextRentalContractToStart(unit) {
  //gives the closest contract to start
  if (!unit || unit == undefined) {
    return null;
  }

  let i = 0;
  let flag = 0;
  let closestContract = {};

  const filteredContracts = unit?.contracts.filter(
    contract =>
      contract.status === contractStatus.CREATED &&
      contract.type != contractTypes.PROPERTY_TITLE
  );

  flag = differenceInMinutes(today, new Date(filteredContracts[0]?.startDate));
  closestContract = filteredContracts[0];

  for (i = 0; i < filteredContracts.length; i++) {
    if (
      differenceInMinutes(today, new Date(filteredContracts[i]?.startDate)) <=
      flag
    ) {
      flag = differenceInMinutes(
        today,
        new Date(filteredContracts[i]?.startDate)
      );
      closestContract = filteredContracts[i];
    }
  }

  return closestContract;
}

export function getContractUnitTenant(unit, contracts) {
  if (!contracts || isEmpty(contracts) || !unit || isEmpty(unit)) {
    return [];
  } else {
    const tenantsGroups = unit.individualGroups.filter(
      group => group.type === profiles.TENANT
    );

    if (!contracts || isEmpty(contracts)) {
      return;
    } else {
      const filteredContract = contracts.filter(
        contract =>
          contract.status === contractStatus.VALID &&
          contract.type != contractTypes.PROPERTY_TITLE
      );

      const contractTenants = filteredContract
        ? filteredContract.map(contract => contract.tenantsId).flat(2)
        : [];

      const individualOfContract = tenantsGroups.find(group =>
        group.members.find(
          member => member.individual.id === contractTenants[0]
        )
      );

      return individualOfContract
        ? individualOfContract.members.filter(
            member => member.individual.profiles[0].type === profiles.TENANT
          )
        : [];
    }
  }
}

export function getContractUnitOwnerResidents(unit, contracts) {
  if (!contracts || isEmpty(contracts) || !unit || isEmpty(unit)) {
    return [];
  } else {
    const ownersGroups = unit.individualGroups.filter(
      group => group.type === profiles.OWNER
    );

    if (!contracts || isEmpty(contracts)) {
      return;
    } else {
      const filteredContract = contracts.filter(
        contract =>
          contract.status === contractStatus.VALID &&
          contract.type === contractTypes.PROPERTY_TITLE
      );

      const contractOwners = filteredContract
        ? filteredContract.map(e => e.coresidentsId).flat(2)
        : [];

      const individualOfContract = ownersGroups.find(group =>
        group.members.find(member => member.individual.id === contractOwners[0])
      );

      return individualOfContract
        ? individualOfContract.members.filter(
            member => member.relationship === 'OTHER'
          )
        : [];
    }
  }
}

export function getContractTenantResidents(unit, contracts) {
  if (!contracts || isEmpty(contracts) || !unit || isEmpty(unit)) {
    return [];
  } else {
    const tenantsGroups = unit.individualGroups.filter(
      group => group.type === profiles.TENANT
    );

    if (!contracts || isEmpty(contracts)) {
      return;
    } else {
      const filteredContract = contracts.filter(
        contract =>
          contract.status === contractStatus.VALID &&
          contract.type != contractTypes.PROPERTY_TITLE
      );

      const contractTenants = filteredContract
        ? filteredContract.map(contract => contract.coresidentsId).flat(2)
        : [];

      const individualOfContract = tenantsGroups.find(group =>
        group.members.find(
          member => member.individual.id === contractTenants[0]
        )
      );

      return individualOfContract
        ? individualOfContract.members.filter(
            member => member.relationship === 'OTHER'
          )
        : [];
    }
  }
}

export function hasCreateUnitFailed(state) {
  return state.units.createUnitHasFailed;
}

export function isDuplicatedLotError(state) {
  const apiErrors = state.units.apiError;
  const duplicatedError = find(apiErrors, ['code', 'resourceAlreadyExists']);
  return duplicatedError != null;
}

export function getCurrentUnitCoordinates(state) {
  const currentCoordinates = state.units.currentUnit.location;
  return currentCoordinates
    ? currentCoordinates
    : {
        latitude: 0,
        longitude: 0
      };
}

export function getUpdatedCurrentUnitCoordinates(state) {
  const coordinates = getCurrentUnitCoordinates(state);
  const updatedCoordinates = state.units.updatedCurrentUnitCoordinates;
  if (updatedCoordinates.latitude === 0 && updatedCoordinates.longitude === 0) {
    return coordinates;
  } else {
    return updatedCoordinates;
  }
}

export function isUnitCoordinatesAlreadySet(state) {
  const updatedCoordinates = getUpdatedCurrentUnitCoordinates(state);
  const location = getCurrentUnitCoordinates(state);
  return (
    (updatedCoordinates.latitude !== 0 && updatedCoordinates.longitude !== 0) ||
    (location.latitude !== 0 && location.longitude)
  );
}

export function getUnitVehiclesForTable(unit) {
  const vehicles = unit.individualGroups.map(group => group.vehicles);
  return flatten(vehicles);
}

export function getValidateContractState(state) {
  return state.contracts.validatedContract;
}

export function findUnitByIdIsInProgress(state) {
  return state.units.findUnitByIdIsInProgress;
}

export const getActiveIndividualGroup = individualGroups => {
  const enabledTenantGroup = individualGroups.find(
    group => group.type === profiles.TENANT && group.status === 'ENABLED'
  );
  const enabledOwnerGroup = individualGroups.find(
    group => group.type === profiles.OWNER && group.status === 'ENABLED'
  );
  return enabledTenantGroup ? enabledTenantGroup : enabledOwnerGroup;
};

const normalizeList = value => {
  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value?.content)) {
    return value.content;
  }

  return [];
};

const idsAreEqual = (left, right) => `${left}` === `${right}`;

const getAssignmentAccessLevelId = assignment =>
  assignment.accessLevelId || assignment.accessLevel?.id;

const getAssignmentSubjectType = assignment =>
  assignment.subjectType ||
  assignment.subject?.type ||
  assignment.subject?.subjectType;

const getAssignmentSubjectId = assignment =>
  assignment.subjectId ||
  assignment.subject?.id ||
  assignment.subject?.subjectId;

export const getUserUnitIds = user => {
  if (!user || !Array.isArray(user.profiles)) {
    return [];
  }

  return uniqueById(
    user.profiles
      .map(profile => profile.unit?.id || profile.unitId)
      .filter(unitId => unitId != null)
      .map(id => ({ id }))
  ).map(unit => unit.id);
};

const getAccessLevelName = (assignment, accessLevels) => {
  const accessLevel = accessLevels.find(level =>
    idsAreEqual(level.id, getAssignmentAccessLevelId(assignment))
  );

  return (
    accessLevel?.name ||
    assignment.accessLevelName ||
    assignment.accessLevel?.name ||
    getAssignmentAccessLevelId(assignment)
  );
};

const decorateAssignments = (assignments, accessLevels) => {
  return assignments.map(assignment => ({
    ...assignment,
    accessLevelName: getAccessLevelName(assignment, accessLevels)
  }));
};

const uniqueById = items =>
  items.reduce((uniqueItems, item) => {
    if (!uniqueItems.find(uniqueItem => idsAreEqual(uniqueItem.id, item.id))) {
      uniqueItems.push(item);
    }

    return uniqueItems;
  }, []);

export function getUnitAccessLevels(state) {
  return normalizeList(state.units.accessLevels);
}

export function getZoneAccessAssignments(state) {
  return normalizeList(state.units.zoneAccessAssignments);
}

export function getCurrentUnitZoneAccessAssignments(state) {
  const currentUnit = getCurrentUnit(state);

  if (!currentUnit) {
    return [];
  }

  const accessLevels = getUnitAccessLevels(state);
  const assignments = getZoneAccessAssignments(state).filter(
    assignment =>
      getAssignmentSubjectType(assignment) === 'UNIT' &&
      idsAreEqual(getAssignmentSubjectId(assignment), currentUnit.id)
  );

  return decorateAssignments(assignments, accessLevels);
}

export function getCurrentUnitResidentZoneAccessAssignments(state) {
  const currentUnit = getCurrentUnit(state);

  if (!currentUnit) {
    return [];
  }

  const accessLevels = getUnitAccessLevels(state);
  const individuals = uniqueById(getAllIndividualsOnUnit(currentUnit));
  const assignments = getZoneAccessAssignments(state);

  return individuals.map(individual => ({
    ...individual,
    zoneAccessAssignments: decorateAssignments(
      assignments.filter(
        assignment =>
          getAssignmentSubjectType(assignment) === 'INDIVIDUAL' &&
          idsAreEqual(getAssignmentSubjectId(assignment), individual.id)
      ),
      accessLevels
    )
  }));
}

export function getIndividualZoneAccessAssignments(state, individualId) {
  if (individualId == null) {
    return [];
  }

  const accessLevels = getUnitAccessLevels(state);
  const assignments = getZoneAccessAssignments(state).filter(
    assignment =>
      getAssignmentSubjectType(assignment) === 'INDIVIDUAL' &&
      idsAreEqual(getAssignmentSubjectId(assignment), individualId)
  );

  return decorateAssignments(assignments, accessLevels);
}

export function getIndividualEffectiveZoneAccessAssignments(state, user) {
  const individualId = user?.id;

  if (individualId == null) {
    return [];
  }

  const accessLevels = getUnitAccessLevels(state);
  const unitIds = getUserUnitIds(user);
  const assignments = getZoneAccessAssignments(state);
  const individualAssignments = assignments.filter(
    assignment =>
      getAssignmentSubjectType(assignment) === 'INDIVIDUAL' &&
      idsAreEqual(getAssignmentSubjectId(assignment), individualId)
  );
  // El backend, con includeInherited=true, devuelve junto a los overrides
  // del individuo las zonas heredadas de COMUNIDAD en la misma llamada —
  // pero esas filas vienen con subjectType='COMMUNITY' (inherited:true),
  // no 'INDIVIDUAL', así que no son parte de individualAssignments.
  const directAssignments = individualAssignments.filter(
    assignment => !assignment.inherited
  );
  const communityInheritedAssignments = assignments
    .filter(
      assignment =>
        assignment.inherited &&
        getAssignmentSubjectType(assignment) === 'COMMUNITY'
    )
    .map(assignment => ({ ...assignment, source: 'COMMUNITY' }));
  const unitInheritedAssignments = assignments
    .filter(
      assignment =>
        getAssignmentSubjectType(assignment) === 'UNIT' &&
        unitIds.some(unitId =>
          idsAreEqual(getAssignmentSubjectId(assignment), unitId)
        )
    )
    .map(assignment => ({
      ...assignment,
      source: 'UNIT',
      inheritedSubjectType: getAssignmentSubjectType(assignment),
      inheritedSubjectId: getAssignmentSubjectId(assignment)
    }));
  const directByAccessLevel = directAssignments.reduce(
    (assignmentsByAccessLevel, assignment) => ({
      ...assignmentsByAccessLevel,
      [getAssignmentAccessLevelId(assignment)]: assignment
    }),
    {}
  );
  const notOverridden = assignment =>
    !directByAccessLevel[getAssignmentAccessLevelId(assignment)];

  // Una misma zona puede heredarse de más de una unidad (o de unidad y
  // comunidad a la vez) — se muestra una sola fila por accessLevelId.
  const effectiveInheritedAssignments = [
    ...unitInheritedAssignments,
    ...communityInheritedAssignments
  ]
    .filter(notOverridden)
    .reduce((deduped, assignment) => {
      const accessLevelId = getAssignmentAccessLevelId(assignment);

      if (
        deduped.some(
          existing =>
            `${getAssignmentAccessLevelId(existing)}` === `${accessLevelId}`
        )
      ) {
        return deduped;
      }

      return [...deduped, assignment];
    }, []);

  return decorateAssignments(
    [
      ...directAssignments.map(assignment => ({
        ...assignment,
        inherited: false
      })),
      ...effectiveInheritedAssignments.map(assignment => ({
        ...assignment,
        inherited: true
      }))
    ],
    accessLevels
  );
}

export function isFindAccessLevelsInProgress(state) {
  return state.units.accessLevelsInProgress;
}

export function isFindZoneAccessAssignmentsInProgress(state) {
  return state.units.zoneAccessAssignmentsInProgress;
}

export function isZoneAccessAssignmentInProgress(state) {
  return state.units.zoneAccessAssignmentInProgress;
}

export function getZoneAccessAssignmentSuccess(state) {
  return state.units.zoneAccessAssignmentSuccess;
}

export function getZoneAccessAssignmentPending(state) {
  return state.units.zoneAccessAssignmentPending;
}

export function getZoneAccessAssignmentHasFailed(state) {
  return state.units.zoneAccessAssignmentHasFailed;
}

export function getZoneAccessAssignmentApiError(state) {
  return state.units.zoneAccessAssignmentApiError;
}

export function getZoneAccessAssignmentMessage(state) {
  return state.units.zoneAccessAssignmentMessage;
}

// Two-indicator sync feedback (Keenvil vs. Servidor de RecoFacial). 'mode'
// tells the panel whether to use these or the single legacy alert above.
export function getZoneAccessAssignmentMode(state) {
  return state.units.zoneAccessAssignmentMode;
}

export function getZoneAccessAssignmentKeenvilSaved(state) {
  return state.units.zoneAccessAssignmentKeenvilSaved;
}

export function getZoneAccessAssignmentRecofacialStatus(state) {
  return state.units.zoneAccessAssignmentRecofacialStatus;
}

export function getZoneAccessAssignmentRecofacialMessage(state) {
  return state.units.zoneAccessAssignmentRecofacialMessage;
}

// Morosidad (access suspensions) — spec-front-morosidad-castleblack.md.
export function getUnitAccessSuspensions(state) {
  return normalizeList(state.units.unitAccessSuspensions);
}

// Confirmado por Network: el episodio trae un `active` boolean explícito
// además de `liftedAt` — se aceptan los dos como señal de "activo" (si el
// back llega a mandar uno sin el otro, no se rompe).
export const isEpisodeActive = episode =>
  Boolean(episode) && (episode.active === true || episode.liftedAt == null);

// "¿La UF está en mora?" = algún episodio GROUP sin levantar, sin pedir un
// endpoint nuevo (igual que las zonas efectivas).
export function getActiveUnitAccessSuspension(state) {
  return (
    getUnitAccessSuspensions(state).find(
      episode => isEpisodeActive(episode) && episode.subjectType === 'GROUP'
    ) || null
  );
}

// All active episodes on this unit, GROUP or INDIVIDUAL — used to highlight
// only the affected residents' cards, not every card in the unit.
export function getActiveUnitAccessSuspensions(state) {
  return getUnitAccessSuspensions(state).filter(isEpisodeActive);
}

/**
 * Ids (as strings) of every individual actually cut by an active episode on
 * this unit: GROUP episodes resolve to that group's members via
 * `unit.individualGroups`; INDIVIDUAL episodes are just their own subjectId.
 */
export function getSuspendedIndividualIds(state, unit) {
  const groups = unit?.individualGroups || [];

  return getActiveUnitAccessSuspensions(state).reduce((ids, episode) => {
    if (episode.subjectType === 'INDIVIDUAL') {
      ids.add(`${episode.subjectId}`);
      return ids;
    }

    const group = groups.find(
      candidate => `${candidate.id}` === `${episode.subjectId}`
    );
    (group?.members || []).forEach(member => {
      const individualId = member?.individual?.id ?? member?.id;
      if (individualId != null) {
        ids.add(`${individualId}`);
      }
    });

    return ids;
  }, new Set());
}

export function isFindUnitAccessSuspensionsInProgress(state) {
  return state.units.unitAccessSuspensionsInProgress;
}

export function isAccessSuspensionInProgress(state) {
  return state.units.accessSuspensionInProgress;
}

export function getAccessSuspensionSuccess(state) {
  return state.units.accessSuspensionSuccess;
}

export function getAccessSuspensionHasFailed(state) {
  return state.units.accessSuspensionHasFailed;
}

export function getAccessSuspensionApiError(state) {
  return state.units.accessSuspensionApiError;
}

export function getAccessSuspensionMessage(state) {
  return state.units.accessSuspensionMessage;
}

export function getAccessSuspensionKeenvilSaved(state) {
  return state.units.accessSuspensionKeenvilSaved;
}

export function getAccessSuspensionRecofacialStatus(state) {
  return state.units.accessSuspensionRecofacialStatus;
}

export function getAccessSuspensionRecofacialMessage(state) {
  return state.units.accessSuspensionRecofacialMessage;
}
